import {
	createBrowserRouter,
	createMemoryRouter,
	RouterProvider,
	Navigate,
	Outlet,
} from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { staticIdentityRoutes } from '@ashn/identity-react/Identity';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import ErrorPage from './components/Errors/ErrorPage';
import PageNotFound from './components/Errors/PageNotFound';
import LandingPage from './components/LandingPage/LandingPage';
import FitnessPage from './components/JoinOurNetwork/FitnessPage';
import RecruitmentPage from './components/Recruitment/RecruitmentPage';
import ResourceGroupListPage from './components/Resources/ResourceGroupListPage';
import ResourceGroupPage from './components/Resources/ResourceGroupPage';
import ResourcePage from './components/Resources/ResourcePage';
import FaqsPage from './components/Faqs/FaqsPage';
import JONGroupListPage from './components/JoinOurNetwork/Providers/JoinOurNetworkGroupListPage';
import JONGroupPage from './components/JoinOurNetwork/Providers/JoinOurNetworkGroupPage';
import Footer from './components/shared/Footer/Footer';
import Header from './components/shared/Header/Header';
import ScrollToTop from './components/shared/ScrollToTop';
import { AREA_ROUTES, TOP_LEVEL_ROUTES } from './scripts/routes';
import { HOME_URL } from './scripts/navigationItems';
import getEnvironment from './utils/getEnvironment';
import StaticContentSkeleton from './components/StaticContentPage/StaticContentSkeleton';

const StaticContentPage = lazy(() =>
	import('./components/StaticContentPage/StaticContentPage')
);

const RootLayout = styled('div')`
	display: grid;
	grid-template-columns: auto;
	grid-template-rows: auto 1fr auto;
	grid-template-areas:
		'header'
		'main'
		'footer';
	min-height: 100vh;

	.header {
		grid-area: header;
	}

	.main {
		grid-area: main;
	}

	.footer {
		grid-area: footer;
	}
`;

const routerCreate =
	process.env.NODE_ENV === 'test' ? createMemoryRouter : createBrowserRouter;

const router = routerCreate([
	{
		path: HOME_URL,
		element: (
			<RootLayout>
				<Header className='header' />
				<Box
					className='main'
					component='main'
					sx={{ backgroundColor: 'background.default' }}
				>
					<ScrollToTop />
					<Outlet />
				</Box>
				<Footer className='footer' />
			</RootLayout>
		),
		children: [
			{
				index: true,
				element: <LandingPage />,
			},
			{
				path: `${TOP_LEVEL_ROUTES.recruitment}/:regionSpecialtyString`,
				element: <RecruitmentPage />,
			},
			{
				path: TOP_LEVEL_ROUTES.features,
				element: <PageNotFound />,
			},
			{
				path: TOP_LEVEL_ROUTES.resources,
				children: [
					{
						path: AREA_ROUTES.providers,
						element: <ResourceGroupListPage />,
					},
					{
						path: AREA_ROUTES.fitnessCenters,
						element: <PageNotFound />,
					},
					{
						path: AREA_ROUTES.members,
						element: <PageNotFound />,
					},
					{
						path: ':groupPageId',
						element: <ResourceGroupPage />,
					},
					{
						path: ':groupPageId/faq',
						element: <FaqsPage />,
					},
					{
						path: ':groupPageId/:pageId',
						element: <ResourcePage />,
					},
				],
			},
			{
				path: TOP_LEVEL_ROUTES.joinOurNetwork,
				children: [
					{
						path: AREA_ROUTES.providers,
						element: <JONGroupListPage />,
					},
					{
						path: AREA_ROUTES.fitnessCenters,
						element: <FitnessPage />,
					},
					{
						path: ':groupPageId',
						element: <JONGroupPage />,
					},
				],
			},
			{
				path: TOP_LEVEL_ROUTES.termsAndConditions,
				element: (
					<Suspense fallback={<StaticContentSkeleton />}>
						<StaticContentPage />
					</Suspense>
				),
			},
			{
				path: TOP_LEVEL_ROUTES.privacy,
				element: (
					<Suspense fallback={<StaticContentSkeleton />}>
						<StaticContentPage type='PrivacyStatement' />
					</Suspense>
				),
			},
			{
				path: TOP_LEVEL_ROUTES.error,
				element: <ErrorPage />,
			},
			{
				path: TOP_LEVEL_ROUTES.pageNotFound,
				element: <PageNotFound />,
			},
			{
				path: 'identity',
				id: 'identity',
				loader: () => {
					const env = getEnvironment();
					let redirectUri = `https://${env}.ashlink.com/auth/callback`;
					if (env === 'prod') {
						redirectUri = 'https://www.ashlink.com/auth/callback';
					}
					return {
						apiEnvironment: env,
						clientId: 'ashlink.code',
						contactURL:
							'https://ashlink.com/ASH/public/Providers/Contact/default.aspx',
						customGenericErrorMessage: `We're sorry, we are unable to confirm your eligibility. If you are a clinical sub-account user, the primary account must register before you can create your account. Please contact the primary account holder. If you feel this is incorrect, please contact us.<br/>For clinical Users:  844-645-2746<br/>For Fitness Users: 877-329-2746, option 4`,
						customerServicePhoneNumber: '844-645-2746',
						customerServicePhoneNumberFitness: '877-329-2746',
						customerServiceOptionForFitness: '4',
						eligibilityURL: null,
						enableSSO: false,
						hasOwnProvider: false,
						ineligibleRegistration: false,
						overrideMainTitleTo: 'Login',
						path: 7,
						redirectUri,
						termsURL: '/termsconditions',
						unauthorizedCallback: () => <Navigate to='/' />,
					};
				},
				children: staticIdentityRoutes,
			},
			{
				path: '*',
				element: <Navigate to={TOP_LEVEL_ROUTES.pageNotFound} />,
			},
		],
	},
]);

function App() {
	return <RouterProvider router={router} />;
}

export default App;
